import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../App';
import { Wrench, CalendarCheck2, HandHelping, Ban, ChevronLeft, Clock, AlertTriangle } from 'lucide-react';
import { todayMx } from '../lib/dateUtils';

const RECORD_TYPES = [
  { value: 'trabajo',    label: 'Trabajo de cliente',              desc: 'Servicio programado con cliente, domicilio y cobro',   icon: Wrench,        color: 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200' },
  { value: 'disponible', label: 'Espacio disponible',              desc: 'Hueco listo para agendar un trabajo',                  icon: CalendarCheck2, color: 'bg-green-100 border-green-300 text-green-900 hover:bg-green-200 dark:bg-green-950/30 dark:border-green-800 dark:text-green-200' },
  { value: 'apoyo',      label: 'Apoyo a otro técnico',            desc: 'Bloquea al técnico que va a ayudar a otro',            icon: HandHelping,   color: 'bg-purple-100 border-purple-300 text-purple-900 hover:bg-purple-200 dark:bg-purple-950/30 dark:border-purple-800 dark:text-purple-200' },
  { value: 'interno',    label: 'Actividad interna o bloqueo',     desc: 'Material, capacitación, comida, ausencia, junta...',   icon: Ban,           color: 'bg-orange-100 border-orange-300 text-orange-900 hover:bg-orange-200 dark:bg-orange-950/30 dark:border-orange-800 dark:text-orange-200' },
];

const TIPO_LABELS = {
  trabajo: { bg: 'bg-red-100 dark:bg-red-950/30', text: 'text-red-800 dark:text-red-200', label: 'Trabajo' },
  disponible: { bg: 'bg-green-100 dark:bg-green-950/30', text: 'text-green-800 dark:text-green-200', label: 'Disponible' },
  apoyo: { bg: 'bg-purple-100 dark:bg-purple-950/30', text: 'text-purple-800 dark:text-purple-200', label: 'Apoyo' },
  interno: { bg: 'bg-orange-100 dark:bg-orange-950/30', text: 'text-orange-800 dark:text-orange-200', label: 'Interno' },
};

const timeToMin = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
const diffMin = (a, b) => Math.max(0, timeToMin(b) - timeToMin(a));
const minToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

function emptyForm(defaults = {}) {
  const today = todayMx();
  return {
    service_date: today,
    start_time: '09:00',
    end_time: '10:00',
    technician_id: '',
    // trabajo
    client_name: '', phones: [''], address: '', address_reference: '',
    service_type: '', description: '', contact_medium: '',
    quoted_cost: 0, anticipo: 0, internal_comments: '',
    ayudante_id: '',
    // apoyo
    apoyo_a_tecnico_id: '',
    // interno
    motivo_interno: '',
    ...defaults,
  };
}

export default function AgendaRegisterDialog({ open, onOpenChange, onCreated, initialData = null, convertFromId = null, forceType = null, skipTypeStep = false }) {
  const [step, setStep] = useState('type'); // 'type' | 'form'
  const [recordType, setRecordType] = useState(forceType || initialData?.record_type || 'trabajo');
  const [form, setForm] = useState(emptyForm(initialData || {}));
  const [tecnicos, setTecnicos] = useState([]);
  const [ayudantes, setAyudantes] = useState([]);
  const [ocupados, setOcupados] = useState([]);
  const [slots, setSlots] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);

  // Cargar técnicos y ayudantes al abrir
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/technicians?tipo=tecnico`).then(r => setTecnicos(r.data)).catch(() => setTecnicos([]));
    axios.get(`${API}/technicians?tipo=ayudante`).then(r => setAyudantes(r.data)).catch(() => setAyudantes([]));
    setStep(skipTypeStep ? 'form' : 'type');
    setRecordType(forceType || initialData?.record_type || 'trabajo');
    setForm(emptyForm(initialData || {}));
    setConflict(null);
    setOcupados([]);
    setSlots([]);
  }, [open, initialData, forceType, skipTypeStep]);

  // Al elegir técnico + fecha (o cambiar duración), cargar horarios del día
  const loadSchedule = useCallback(async () => {
    if (!form.technician_id || !form.service_date) { setOcupados([]); setSlots([]); return; }
    const durMin = Math.max(15, diffMin(form.start_time, form.end_time) || 60);
    try {
      const r = await axios.get(`${API}/agenda/available-slots`, { params: { technician_id: form.technician_id, service_date: form.service_date, duration_min: durMin, ...(convertFromId ? { exclude_id: convertFromId } : {}) } });
      setOcupados(r.data.ocupados || []);
      setSlots(r.data.available_slots || []);
    } catch { /* silencioso */ }
  }, [form.technician_id, form.service_date, form.start_time, form.end_time, convertFromId]);

  useEffect(() => { if (step === 'form') loadSchedule(); }, [loadSchedule, step]);

  const pickSlot = (slot) => {
    // Usa duración actual del form; si supera el slot, ajusta al slot
    const durMin = Math.max(15, diffMin(form.start_time, form.end_time) || 60);
    const slotDur = timeToMin(slot.end_time) - timeToMin(slot.start_time);
    const useDur = Math.min(durMin, slotDur);
    setForm(prev => ({ ...prev, start_time: slot.start_time, end_time: minToTime(timeToMin(slot.start_time) + useDur) }));
    setConflict(null);
    toast.success(`Horario aplicado: ${slot.start_time} - ${minToTime(timeToMin(slot.start_time) + useDur)}`);
  };

  const chooseType = (v) => { setRecordType(v); setStep('form'); };

  const backToType = () => setStep('type');

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setConflict(null);
    try {
      const tec = tecnicos.find(t => t.id === form.technician_id);
      const payload = {
        record_type: recordType,
        service_date: form.service_date,
        start_time: form.start_time,
        end_time: form.end_time,
        technician_id: form.technician_id || null,
        technician_name: tec?.name || '',
        duration_minutes: diffMin(form.start_time, form.end_time) || 60,
        assigned_by: '', // backend usa current_user
        assigned_by_id: '',
        quoted_cost: 0,
        phones: [],
        status: 'pendiente',
      };
      if (recordType === 'trabajo') {
        Object.assign(payload, {
          client_name: form.client_name,
          phones: (form.phones || []).filter(Boolean),
          address: form.address,
          address_reference: form.address_reference || '',
          service_type: form.service_type,
          description: form.description || '',
          contact_medium: form.contact_medium || '',
          quoted_cost: parseFloat(form.quoted_cost) || 0,
          anticipo: parseFloat(form.anticipo) || 0,
          internal_comments: form.internal_comments || '',
          ayudante_id: form.ayudante_id || null,
        });
      } else if (recordType === 'disponible') {
        payload.description = form.description || 'Espacio para agendar trabajo';
      } else if (recordType === 'apoyo') {
        const pt = tecnicos.find(t => t.id === form.apoyo_a_tecnico_id);
        Object.assign(payload, {
          apoyo_a_tecnico_id: form.apoyo_a_tecnico_id || null,
          apoyo_a_tecnico_name: pt?.name || '',
          description: form.description || `Apoyo a ${pt?.name || ''}`,
        });
      } else if (recordType === 'interno') {
        Object.assign(payload, {
          motivo_interno: form.motivo_interno || 'Actividad interna',
          description: form.description || form.motivo_interno,
          internal_comments: form.internal_comments || '',
        });
      }
      await axios.post(`${API}/services`, payload);
      // Si es conversión desde un espacio disponible, eliminar el registro original
      if (convertFromId) {
        try { await axios.delete(`${API}/services/${convertFromId}`); } catch { /* ignore */ }
        toast.success('Espacio convertido a trabajo');
      } else {
        toast.success('Registro creado');
      }
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === 'object' && detail?.conflict) {
        setConflict(detail);
        toast.error(detail.message || 'Conflicto de horario');
        // recargar slots
        loadSchedule();
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error al guardar'));
      }
    } finally {
      setSaving(false);
    }
  };

  const validForm = useMemo(() => {
    if (!form.technician_id || !form.service_date || !form.start_time || !form.end_time) return false;
    if (timeToMin(form.end_time) <= timeToMin(form.start_time)) return false;
    if (recordType === 'trabajo') return !!(form.client_name && form.address && form.service_type);
    if (recordType === 'apoyo') return !!form.apoyo_a_tecnico_id;
    if (recordType === 'interno') return !!form.motivo_interno;
    return true; // disponible
  }, [form, recordType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="agenda-register-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'form' && !skipTypeStep && (
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={backToType} data-testid="register-back-btn"><ChevronLeft className="h-4 w-4" /></Button>
            )}
            {convertFromId ? 'Convertir espacio disponible a trabajo' : 'Nuevo registro en agenda'}
          </DialogTitle>
          <DialogDescription>
            {convertFromId
              ? 'Se conserva fecha, hora y técnico. Al guardar, el espacio disponible se elimina automáticamente.'
              : (step === 'type' ? 'Selecciona el tipo de registro que quieres crear.' : `Tipo: ${RECORD_TYPES.find(t => t.value === recordType)?.label}`)}
          </DialogDescription>
        </DialogHeader>

        {step === 'type' ? (
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            {RECORD_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => chooseType(t.value)}
                  className={`text-left rounded-lg border-2 p-4 transition ${t.color}`}
                  data-testid={`type-${t.value}`}
                >
                  <div className="flex items-center gap-3 mb-1">
                    <Icon className="h-6 w-6" />
                    <span className="font-semibold">{t.label}</span>
                  </div>
                  <p className="text-xs opacity-80">{t.desc}</p>
                </button>
              );
            })}
          </div>
        ) : (
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-4">
            {/* FORM */}
            <div className="md:col-span-2 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Fecha *</Label><Input type="date" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} required data-testid="reg-date" /></div>
                <div><Label className="text-xs">Hora inicio *</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="reg-start" /></div>
                <div><Label className="text-xs">Hora fin *</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required data-testid="reg-end" /></div>
              </div>
              <div className="text-xs text-muted-foreground">Duración calculada: <strong>{diffMin(form.start_time, form.end_time)} min</strong></div>

              <div><Label className="text-xs">Técnico *</Label>
                <Select value={form.technician_id} onValueChange={(v) => setForm({ ...form, technician_id: v })}>
                  <SelectTrigger data-testid="reg-tech"><SelectValue placeholder="Selecciona un técnico" /></SelectTrigger>
                  <SelectContent>{tecnicos.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {recordType === 'trabajo' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Cliente *</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required data-testid="reg-client" /></div>
                    <div><Label className="text-xs">Teléfono</Label><Input value={form.phones[0]} onChange={(e) => setForm({ ...form, phones: [e.target.value] })} data-testid="reg-phone" /></div>
                  </div>
                  <div><Label className="text-xs">Domicilio *</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required data-testid="reg-address" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Servicio *</Label><Input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="Plomería, AC..." required data-testid="reg-service" /></div>
                    <div><Label className="text-xs">Medio de contacto</Label>
                      <Select value={form.contact_medium} onValueChange={(v) => setForm({ ...form, contact_medium: v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Whatsapp">WhatsApp</SelectItem><SelectItem value="Llamada">Llamada</SelectItem>
                          <SelectItem value="Facebook">Facebook</SelectItem><SelectItem value="Referido">Referido</SelectItem>
                          <SelectItem value="Presencial">Presencial</SelectItem><SelectItem value="Otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-xs">Descripción</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">Costo cotizado</Label><Input type="number" value={form.quoted_cost} onChange={(e) => setForm({ ...form, quoted_cost: e.target.value })} /></div>
                    <div><Label className="text-xs">Anticipo</Label><Input type="number" value={form.anticipo} onChange={(e) => setForm({ ...form, anticipo: e.target.value })} /></div>
                    <div><Label className="text-xs">Ayudante (opcional)</Label>
                      <Select value={form.ayudante_id || 'none'} onValueChange={(v) => setForm({ ...form, ayudante_id: v === 'none' ? '' : v })}>
                        <SelectTrigger data-testid="reg-ayudante"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Sin ayudante —</SelectItem>
                          {ayudantes.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-xs">Comentarios internos</Label><Textarea rows={2} value={form.internal_comments} onChange={(e) => setForm({ ...form, internal_comments: e.target.value })} /></div>
                </>
              )}

              {recordType === 'disponible' && (
                <>
                  <div className="rounded-md border bg-green-50 dark:bg-green-950/20 p-3 text-xs">Este bloque quedará marcado como <strong>“Disponible para agendar”</strong>. No pide cliente, teléfono, domicilio ni cobro. No bloquea el horario del técnico.</div>
                  <div><Label className="text-xs">Descripción / notas</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Espacio para agendar trabajo" /></div>
                </>
              )}

              {recordType === 'apoyo' && (
                <>
                  <div className="rounded-md border bg-purple-50 dark:bg-purple-950/20 p-3 text-xs">El técnico principal seleccionado arriba <strong>irá a ayudar</strong> al técnico que elijas aquí. Durante ese horario aparecerá como ocupado.</div>
                  <div><Label className="text-xs">Ayudará a *</Label>
                    <Select value={form.apoyo_a_tecnico_id} onValueChange={(v) => setForm({ ...form, apoyo_a_tecnico_id: v })}>
                      <SelectTrigger data-testid="reg-apoyo"><SelectValue placeholder="Selecciona técnico a apoyar" /></SelectTrigger>
                      <SelectContent>{tecnicos.filter(t => t.id !== form.technician_id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Descripción del apoyo</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ayudar en instalación de minisplit..." /></div>
                </>
              )}

              {recordType === 'interno' && (
                <>
                  <div className="rounded-md border bg-orange-50 dark:bg-orange-950/20 p-3 text-xs">Bloqueo de horario del técnico por una actividad interna. No pide cliente ni cobro.</div>
                  <div><Label className="text-xs">Motivo *</Label>
                    <Select value={form.motivo_interno} onValueChange={(v) => setForm({ ...form, motivo_interno: v })}>
                      <SelectTrigger data-testid="reg-motivo"><SelectValue placeholder="Selecciona motivo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Ir por material">Ir por material</SelectItem>
                        <SelectItem value="Camioneta al taller">Camioneta al taller</SelectItem>
                        <SelectItem value="Capacitación">Capacitación</SelectItem>
                        <SelectItem value="Traslado">Traslado</SelectItem>
                        <SelectItem value="Junta">Junta</SelectItem>
                        <SelectItem value="Comida">Comida</SelectItem>
                        <SelectItem value="Actividad de oficina">Actividad de oficina</SelectItem>
                        <SelectItem value="Ausencia">Ausencia</SelectItem>
                        <SelectItem value="Permiso">Permiso</SelectItem>
                        <SelectItem value="Otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Descripción / detalles</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div><Label className="text-xs">Comentarios</Label><Textarea rows={2} value={form.internal_comments} onChange={(e) => setForm({ ...form, internal_comments: e.target.value })} /></div>
                </>
              )}

              {conflict && (
                <div className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/20 p-3 space-y-2" data-testid="conflict-alert">
                  <div className="flex items-center gap-2 text-red-800 dark:text-red-200 font-semibold text-sm">
                    <AlertTriangle className="h-4 w-4" />Horario no disponible
                  </div>
                  <p className="text-xs">{conflict.message}</p>
                  {conflict.conflict && (
                    <div className="text-xs bg-white/60 dark:bg-black/30 rounded p-2">
                      <div><strong>{conflict.conflict.client_name || conflict.conflict.motivo_interno || conflict.conflict.record_type}</strong> · {conflict.conflict.start_time}-{conflict.conflict.end_time} · <span className="uppercase">{conflict.conflict.status}</span></div>
                    </div>
                  )}
                  {slots.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold mb-1">Horarios disponibles (click para aplicar):</p>
                      <div className="flex flex-wrap gap-1">
                        {slots.map((s, i) => (
                          <button type="button" key={i} onClick={() => pickSlot(s)} className="px-2 py-1 rounded bg-green-200 hover:bg-green-300 text-green-900 text-xs font-medium" data-testid={`slot-${i}`}>
                            {s.start_time}-{s.end_time} ({s.duration_min}m)
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PANEL LATERAL: Horarios del técnico */}
            <div className="md:col-span-1 border-l pl-3">
              <h3 className="font-semibold text-sm flex items-center gap-1"><Clock className="h-4 w-4" />Horarios del técnico</h3>
              <p className="text-[10px] text-muted-foreground mb-2">{form.service_date}</p>
              {!form.technician_id ? (
                <p className="text-xs text-muted-foreground">Selecciona técnico y fecha para ver la agenda del día.</p>
              ) : (
                <div className="space-y-1">
                  {ocupados.length === 0 && slots.length === 0 && (
                    <p className="text-xs text-muted-foreground">Sin datos.</p>
                  )}
                  {ocupados.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-[10px] uppercase text-muted-foreground">Ocupados</p>
                      {ocupados.map((o, i) => {
                        const tl = TIPO_LABELS[o.record_type] || TIPO_LABELS.trabajo;
                        return (
                          <div key={i} className={`rounded p-1.5 text-[11px] ${tl.bg} ${tl.text}`} data-testid={`sched-busy-${i}`}>
                            <div className="flex justify-between font-semibold"><span>{o.start_time}-{o.end_time}</span><span className="uppercase text-[9px]">{tl.label}</span></div>
                            <div className="truncate">{o.client_name || o.motivo_interno || (o.record_type === 'apoyo' ? `Apoya a ${o.apoyo_a_tecnico_name}` : o.service_type)}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {slots.length > 0 && (
                    <div className="space-y-1 pt-2">
                      <p className="text-[10px] uppercase text-muted-foreground">Disponibles (click para aplicar)</p>
                      {slots.map((s, i) => (
                        <button key={i} type="button" onClick={() => pickSlot(s)} className="w-full rounded p-1.5 text-[11px] bg-emerald-100 hover:bg-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200 text-left" data-testid={`sched-free-${i}`}>
                          <div className="flex justify-between font-semibold"><span>{s.start_time}-{s.end_time}</span><span className="text-[9px]">{s.duration_min}m</span></div>
                          <div className="text-[10px] opacity-80">Disponible para agendar</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="md:col-span-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={!validForm || saving} data-testid="reg-submit">{saving ? 'Guardando...' : (convertFromId ? 'Convertir a trabajo' : 'Guardar registro')}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
